import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'merge-left-utils'
import { Button, DateRangePicker, Form, SelectPicker, Tag } from 'rsuite'
import { RSUITE_DATEPICKER_LOCALE } from '@@partner:config/rsuite'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'
import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import SelectAssemblerPicker from '@components/common/commonFormInput/SelectAssemblerPicker'
import { prepareAssemblingWorkShiftFilter } from '@@partner:helpers/assemblingWorkshift'
import './AssemblingShiftsFilter.scss'

export type AssemblingShiftsFiltersType = {
  dateRange: [Date, Date] | null
  assemblerIds: GQL_ID[]
}

type AssemblingShiftsFilterProps = {
  onFilterChanged: (filters: NestedFilter) => void
}

const defaultFilter: AssemblingShiftsFiltersType = {
  dateRange: null,
  assemblerIds: []
}

export default function AssemblingShiftsFilter ({ onFilterChanged }: AssemblingShiftsFilterProps) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  const [ state, setState ] = useState<AssemblingShiftsFiltersType>(defaultFilter)

  useEffect(() => {
    const searchParams = getSearchParams()
    const newState: AssemblingShiftsFiltersType = mergeLeft(defaultFilter, searchParams)
    setState(newState)
    onFilterChanged(prepareAssemblingWorkShiftFilter(newState))
  }, [ getSearchParams, onFilterChanged ])

  const handleFormSubmit = useCallback(() => {
    setSearchParams(state)
    onFilterChanged(prepareAssemblingWorkShiftFilter(state))
  }, [ onFilterChanged, state, setSearchParams ])

  const handleFormChange = (formValue: Partial<AssemblingShiftsFiltersType>) => {
    setState((state) => mergeLeft(state, formValue))
  }

  const handleDateRangeChange = useCallback((range: [Date, Date] | null) => {
    setState(prev => ({ ...prev, dateRange: range }))
  }, [])

  return (
    <Form
      formValue={state}
      className="AssemblingShiftsFilter"
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
    >
      <Form.Group className="AssemblingShiftsFilter__Filters">
        <DateRangePicker
          value={state.dateRange}
          onChange={handleDateRangeChange}
          locale={RSUITE_DATEPICKER_LOCALE}
          format="dd.MM.yyyy"
          placeholder="Выберите период"
          size="md"
          cleanable
        />
        <SelectAssemblerPicker
          isMultiple={true}
          value={state.assemblerIds}
          onChange={(value) => {
            setState(prev => ({ ...prev, assemblerIds: value || [] }))
          }}
        />
      </Form.Group>

      <div className="AssemblingShiftsFilter__Controls">
        <Button
          onClick={handleFormSubmit}
          size="md"
          color="green"
          appearance="primary"
        >
          Поиск
        </Button>
      </div>
    </Form>
  )
}